import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { readingImage, arrowRightIcon } from "../../../assets";
import { normalizeLanguage } from "../../../utils/language";
import { createSlug } from "../../../utils/slug";
import { useBlogList } from "../hooks/useBlogList";
import type { BlogListItem } from "../types";

const createBlogUrl = (blog: BlogListItem, language: "tr" | "en") => {
  return `/blog-yazilari/${createSlug(blog.baslik, language)}`;
};

function BlogHighlights() {
  const { i18n, t } = useTranslation();
  const language = normalizeLanguage(i18n.language);
  const { data, isError } = useBlogList(language);

  const topBlogs = useMemo(() => {
    const blogs = data?.blog ?? [];
    return [...blogs].sort((a, b) => b.okunma_sayisi - a.okunma_sayisi).slice(0, 5);
  }, [data?.blog]);

  if (isError) {
    return <div className="text-red-500 text-center">{t("blogPage.errorLoading")}</div>;
  }

  return (
    <div>
      <BlogList topBlogs={topBlogs} language={language} />
    </div>
  );
}

interface BlogListProps {
  topBlogs: BlogListItem[];
  language: "tr" | "en";
}

function BlogList({ topBlogs, language }: BlogListProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-customWhite mt-48 relative md:pb-28">
      <section id="premiumsecton">
        <div className="custom-shape-divider-top-1726687530">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="shape-fill"
            ></path>
          </svg>
        </div>
        <div className="blog-article w-[90%] md:w-[75%] mx-auto pt-44 pb-20">
          <div className="blog-article-container">
            <div className="blog-article-head text-left py-6 text-4xl">
              {t("blog.popularPosts")}
            </div>

            <div className="blog-article-content grid grid-cols-1 md:grid-cols-2 gap-4 text-white">
              <div className="blog-article-img my-auto">
                <img src={readingImage} alt="Reading" className="h-auto" loading="lazy" />
              </div>

              <div className="blog-article-box-container text-black">
                {topBlogs.map((blog) => (
                  <Link to={createBlogUrl(blog, language)} key={blog.blog_id}>
                    <div className="blog-article-box mt-4 h-20 transition-all delay-100 hover:h-24 hover:shadow-custom-rounded-hover border flex items-center p-4 border-black shadow-custom-rounded shadow-black">
                      <div className="blog-article-box-title flex-1">{blog.baslik}</div>
                      <div className="blog-article-box-icon text-right">
                        <img
                          src={arrowRightIcon}
                          alt="Arrow icon"
                          loading="lazy"
                          className="w-14"
                        />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogHighlights;
